import { Box, Button, Group, NumberInput, Text } from "@mantine/core";
import { useState } from "react";
import type { AssistantUsageText } from "@/locales/assistant-usage";
import type { ModelRateDto, ModelRateUpdate } from "@/types/ai-assistant-usage";

interface Props {
	rate: ModelRateDto;
	t: AssistantUsageText;
	disabled: boolean;
	onSave: (model: string, body: ModelRateUpdate) => Promise<void>;
}

const RATE_DECIMALS = 4;
const UNKNOWN_MODEL = "unknown"; // sama dengan UNKNOWN_MODEL di server (cap.ledger.repo.ts)
const MOCK_MODEL = "mock";
// Cukup lebar agar "model · Chat Claude (token)" muat satu baris; nama model panjang tetap boleh turun baris.
const LABEL_WIDTH = 280;
const toNum = (v: number | string): number | null =>
	typeof v === "number" && Number.isFinite(v) ? v : null;

/** Penjelasan singkat per model; hanya `unknown` dan `mock` dikhususkan, sisanya menurut jenis. */
function describeModel(rate: ModelRateDto, t: AssistantUsageText): string {
	if (rate.model === UNKNOWN_MODEL) return t.rateDescUnknown;
	if (rate.model === MOCK_MODEL) return t.rateDescMock;
	return rate.kind === "chat" ? t.rateDescChat : t.rateDescVoice;
}

/** Satu baris tarif model: suara per menit, chat per 1 juta token (masuk & keluar harus diisi sekaligus). */
export function CapRateRow({ rate, t, disabled, onSave }: Props) {
	const isVoice = rate.kind === "voice";
	const [perMinute, setPerMinute] = useState<number | string>(
		rate.usdPerMinute ?? "",
	);
	const [input, setInput] = useState<number | string>(
		rate.inputUsdPerMTok ?? "",
	);
	const [output, setOutput] = useState<number | string>(
		rate.outputUsdPerMTok ?? "",
	);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(
		null,
	);

	const body: ModelRateUpdate = {
		kind: rate.kind,
		usdPerMinute: isVoice ? toNum(perMinute) : null,
		inputUsdPerMTok: isVoice ? null : toNum(input),
		outputUsdPerMTok: isVoice ? null : toNum(output),
	};
	const incomplete =
		!isVoice &&
		(body.inputUsdPerMTok === null) !== (body.outputUsdPerMTok === null);

	const save = async () => {
		setSaving(true);
		setMessage(null);
		try {
			await onSave(rate.model, body);
			setMessage({ ok: true, text: t.rateSaved });
		} catch (err) {
			setMessage({
				ok: false,
				text: err instanceof Error ? err.message : t.loadFailed,
			});
		} finally {
			setSaving(false);
		}
	};

	const field = (
		label: string,
		value: number | string,
		set: (v: number | string) => void,
	) => (
		<NumberInput
			label={label}
			size="xs"
			min={0}
			decimalScale={RATE_DECIMALS}
			placeholder={t.rateUnset}
			value={value}
			onChange={(v) => {
				setMessage(null);
				set(v);
			}}
			disabled={disabled}
			w={190}
		/>
	);

	return (
		<Group align="flex-end" gap="sm" data-rate-model={rate.model}>
			<Box w={LABEL_WIDTH}>
				<Text size="sm" fw={600} data-rate-label>
					{rate.model}
					<Text
						span
						size="xs"
						c="dimmed"
						fw={400}
						style={{ whiteSpace: "nowrap" }}
					>
						{" · "}
						{rate.kind === "chat" ? t.chat : t.voice}
					</Text>
				</Text>
				<Text size="xs" c="dimmed" data-rate-desc>
					{describeModel(rate, t)}
				</Text>
			</Box>
			{isVoice ? (
				field(t.ratePerMinute, perMinute, setPerMinute)
			) : (
				<>
					{field(t.rateInputMTok, input, setInput)}
					{field(t.rateOutputMTok, output, setOutput)}
				</>
			)}
			<Button
				size="xs"
				onClick={save}
				loading={saving}
				disabled={disabled || incomplete}
			>
				{t.rateSave}
			</Button>
			{incomplete && (
				<Text size="xs" c="orange">
					{t.rateIncomplete}
				</Text>
			)}
			{message && (
				<Text size="xs" c={message.ok ? "green" : "red"}>
					{message.text}
				</Text>
			)}
		</Group>
	);
}
